<template>
  <v-container fluid class="pa-6">
    <v-row justify="center">
      <v-col cols="12" md="10">
        <h1 class="mb-4">Admin Tool</h1>

        <v-tabs v-model="activeTab" color="primary" class="mb-4">
          <v-tab value="webinare">Webinare</v-tab>
          <v-tab value="user">User</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- ===================== TAB: WEBINARE ===================== -->
          <v-window-item value="webinare">
            <v-btn
              v-if="hasRouteSelection"
              class="mb-4"
              variant="outlined"
              @click="goToOverview"
            >
              Zurück zur Übersicht
            </v-btn>

            <v-card v-if="!hasRouteSelection" class="mb-4" variant="outlined">
              <v-card-title class="d-flex justify-space-between align-center">
                <span>Bestehende Webinare</span>
                <v-btn
                  size="small"
                  variant="outlined"
                  :loading="isWebinareLoading"
                  :disabled="isWebinareLoading"
                  @click="loadWebinare"
                >
                  Neu laden
                </v-btn>
              </v-card-title>
              <v-card-text>
                <div
                  v-if="isWebinareLoading"
                  class="d-inline-flex align-center ga-2 text-medium-emphasis"
                >
                  <v-progress-circular indeterminate size="18" width="2" color="primary" />
                  <span>Webinare werden geladen...</span>
                </div>
                <div v-else-if="webinarGroups.length === 0" class="text-medium-emphasis">
                  Keine Webinare gefunden.
                </div>
                <v-list v-else density="compact">
                  <v-list-item
                    v-for="webinar in webinarGroups"
                    :key="webinar.webi_id"
                    :active="selectedWebinarId === webinar.webi_id"
                    @click="selectWebinar(webinar)"
                  >
                    <v-list-item-title>
                      {{ webinar.webi_title }} (ID: {{ webinar.webi_id }})
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Dozent: {{ webinar.dozent_name || '-' }} · Termine: {{ webinar.termine.length }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>

            <v-card v-if="selectedWebinar" class="mb-4" variant="outlined">
              <v-card-title class="text-h4 font-weight-black">
                {{ selectedWebinar.webi_title || `Webinar ${selectedWebinar.webi_id}` }}
              </v-card-title>
              <div class="px-4 pt-1 d-flex align-center flex-wrap ga-2">
                <div class="text-h6 font-weight-bold">
                  {{ selectedWebinar.dozent_name || 'Dozent' }}
                </div>
                <v-btn
                  size="small"
                  variant="text"
                  class="px-0 text-none"
                  @click="toggleDozentEdit"
                >
                  {{ showDozentEdit ? 'Bearbeiten ausblenden' : 'Bearbeiten' }}
                </v-btn>
              </div>
              <v-card-text>
                <v-expand-transition>
                  <div v-if="showDozentEdit">
                    <v-row class="mb-2">
                      <v-col cols="12" md="3">
                        <v-text-field v-model="dozentEdit.name" label="Dozent Name" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field v-model="dozentEdit.email" label="Dozent E-Mail" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field v-model="dozentEdit.image" label="Dozent Bild" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model.number="dozentEdit.user_id"
                          type="number"
                          label="Verknüpfte User-ID"
                        />
                        <v-btn
                          size="small"
                          variant="text"
                          class="px-0"
                          @click="openUserSearch(dozentEdit.user_id || dozentEdit.email || dozentEdit.name)"
                        >
                          Zur User-Suche
                        </v-btn>
                      </v-col>
                    </v-row>
                    <div class="d-flex flex-wrap ga-2 mb-3">
                      <v-btn
                        size="small"
                        color="primary"
                        :disabled="!dozentEdit.id"
                        @click="updateDozent"
                      >
                        Dozent speichern
                      </v-btn>
                      <v-chip v-if="dozentEdit.user_id" size="small" variant="outlined">
                        User-ID: {{ dozentEdit.user_id }}
                      </v-chip>
                    </div>
                  </div>
                </v-expand-transition>
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  Termine ({{ terminStats.length }})
                </div>
                <div v-if="terminStats.length === 0" class="text-medium-emphasis mb-3">
                  Keine Termine für dieses Webinar. Lege unten einen Termin an.
                </div>
                <v-row class="mb-3">
                  <v-col
                    v-for="stat in terminStats"
                    :key="stat.wter_id"
                    cols="12"
                    md="4"
                  >
                    <v-card variant="tonal" class="h-100" @click="selectTermin(stat)">
                      <v-card-text>
                        <div class="text-subtitle-2">
                          Termin {{ stat.wter_id }}
                        </div>
                        <div class="text-body-2">
                          Beginn: {{ stat.wter_beginn || '-' }}
                        </div>
                        <div class="text-body-2">
                          Teilnehmer gesamt: {{ stat.teilnehmer_count }}
                        </div>
                        <div class="text-body-2">
                          User-Admins: {{ stat.teilnehmer_user_admin_count }}
                        </div>
                        <div class="text-body-2">
                          User-Dozenten: {{ stat.teilnehmer_user_dozent_count }}
                        </div>
                        <div class="d-flex flex-column ga-2 mt-4">
                          <v-btn
                            size="small"
                            color="primary"
                            block
                            :active="selectedTerminId === stat.wter_id"
                            @click.stop="selectTermin(stat)"
                          >
                            Teilnehmer öffnen ({{ stat.teilnehmer_count }})
                          </v-btn>
                          <div class="d-flex flex-wrap ga-2">
                            <v-btn
                              v-if="stat.dozentLink"
                              size="small"
                              color="green"
                              @click.stop="openExternalLink(stat.dozentLink)"
                            >
                              Dozenten View
                            </v-btn>
                            <v-btn
                              v-if="stat.dozentLink"
                              size="small"
                              variant="outlined"
                              color="green"
                              @click.stop="copyToClipboard(stat.dozentLink, 'Dozenten-Link kopiert.')"
                            >
                              Dozenten Link kopieren
                            </v-btn>
                          </div>
                          <div class="d-flex flex-wrap ga-2">
                            <v-btn
                              v-if="stat.superLink"
                              size="small"
                              color="orange-darken-2"
                              @click.stop="openExternalLink(stat.superLink)"
                            >
                              Admin View
                            </v-btn>
                            <v-btn
                              v-if="stat.superLink"
                              size="small"
                              variant="outlined"
                              color="orange-darken-2"
                              @click.stop="copyToClipboard(stat.superLink, 'Admin-Link kopiert.')"
                            >
                              Admin Link kopieren
                            </v-btn>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card v-if="selectedTerminId" class="mb-4" variant="outlined">
              <v-card-title class="d-flex flex-wrap justify-space-between align-center ga-2">
                <div>
                  Teilnehmer zu Termin {{ selectedTerminId }}
                  <span v-if="selectedTermin?.wter_beginn" class="text-medium-emphasis">
                    - {{ selectedTermin.wter_beginn }}
                  </span>
                </div>
                <div class="d-flex flex-wrap ga-2">
                  <v-btn
                    size="small"
                    variant="outlined"
                    :loading="isTeilnehmerLoading"
                    :disabled="isTeilnehmerLoading"
                    @click="loadTeilnehmer"
                  >
                    Neu laden
                  </v-btn>
                  <v-btn
                    v-if="canCreateTeilnehmer"
                    size="small"
                    color="primary"
                    @click="openTeilnehmerDialog"
                  >
                    Teilnehmer hinzufügen
                  </v-btn>
                </div>
              </v-card-title>
              <v-card-text>
                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  Lege zuerst den Teilnehmer an. Einen User kannst du danach unten am
                  Teilnehmer über das Feld „User-ID" verknüpfen
                  (Suche im Tab „User").
                </v-alert>
                <div
                  v-if="isTeilnehmerLoading"
                  class="d-inline-flex align-center ga-2 text-medium-emphasis mb-3"
                >
                  <v-progress-circular indeterminate size="18" width="2" color="primary" />
                  <span>Teilnehmer werden geladen...</span>
                </div>
                <div v-else-if="teilnehmerList.length === 0" class="text-medium-emphasis mb-3">
                  Keine Teilnehmer vorhanden.
                </div>
                <v-expansion-panels
                  v-else
                  v-model="openTeilnehmerPanels"
                  multiple
                  variant="accordion"
                >
                  <v-expansion-panel
                    v-for="row in teilnehmerEditRows"
                    :key="row.wteil_id"
                    :value="row.wteil_id"
                  >
                    <v-expansion-panel-title>
                      <div :class="['w-100 d-flex flex-column ga-2 py-1', getTeilnehmerMarkerClass(row)]">
                        <div class="text-subtitle-2">
                          {{ row.wteil_pers_name || 'Teilnehmer' }} (ID: {{ row.wteil_id }})
                        </div>
                        <div class="d-flex flex-wrap ga-2 text-body-2 text-medium-emphasis">
                          <span v-if="row.wteil_pers_email">{{ row.wteil_pers_email }}</span>
                          <span v-if="Number(row.wteil_user_id || 0) > 0">User-ID: {{ row.wteil_user_id }}</span>
                          <span v-if="Number(row.wteil_user_id || 0) === 0">ohne User-ID</span>
                        </div>
                        <div class="d-flex flex-wrap ga-2">
                          <v-chip
                            v-if="Number(row.display_is_admin || 0) === 1"
                            size="small"
                            color="primary"
                            variant="flat"
                          >
                            Admin
                          </v-chip>
                          <v-chip
                            v-if="Number(row.display_is_dozent || 0) === 1"
                            size="small"
                            color="deep-purple"
                            variant="flat"
                          >
                            Dozent
                          </v-chip>
                        </div>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div class="mb-4">
                        <div class="text-caption mb-1">
                          {{ getTeilnehmerLinkLabel(row) }}
                        </div>
                        <div
                          v-if="buildTeilnehmerLink(row)"
                          class="d-flex flex-wrap align-center ga-2"
                        >
                          <v-btn
                            size="small"
                            color="primary"
                            @click="openExternalLink(buildTeilnehmerLink(row))"
                          >
                            Link öffnen
                          </v-btn>
                          <v-btn
                            size="small"
                            variant="outlined"
                            @click="copyToClipboard(buildTeilnehmerLink(row), `${getTeilnehmerLinkLabel(row)} kopiert.`)"
                          >
                            Link kopieren
                          </v-btn>
                          <span class="text-body-2 text-medium-emphasis">
                            {{ buildTeilnehmerLink(row) }}
                          </span>
                        </div>
                        <div v-else class="text-body-2 text-medium-emphasis">
                          Kein Link verfügbar.
                        </div>
                      </div>
                      <v-row>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="row.wteil_user_id"
                            type="number"
                            label="User-ID"
                          />
                          <v-btn
                            size="small"
                            variant="text"
                            class="px-0"
                            @click="openUserSearch(row.wteil_user_id || row.wteil_pers_email || row.wteil_pers_name)"
                          >
                            Zur User-Suche
                          </v-btn>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="row.wteil_pers_name"
                            label="Name"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="row.wteil_pers_email"
                            label="E-Mail"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-switch
                            v-model="row.wteil_is_dozent"
                            :true-value="1"
                            :false-value="0"
                            color="primary"
                            label="Als Dozent markieren"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-switch
                            v-model="row.wteil_is_admin"
                            :true-value="1"
                            :false-value="0"
                            color="primary"
                            label="Termin-Admin"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                      <div class="mt-4">
                        <v-btn
                          color="primary"
                          :loading="isTeilnehmerSaving(row.wteil_id)"
                          :disabled="isTeilnehmerSaving(row.wteil_id)"
                          @click="updateTeilnehmer(row)"
                        >
                          Teilnehmer Daten speichern
                        </v-btn>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>

            <v-expansion-panels v-model="openPanels" multiple>
              <v-expansion-panel v-if="selectedWebinar && !hasExistingDozentForSelectedWebinar">
                <v-expansion-panel-title>Dozent anlegen</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="dozent.name" label="Name" />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="dozent.email" label="E-Mail" />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="dozent.image" label="Bild-Datei (optional)" />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model.number="dozent.user_id"
                        type="number"
                        label="Verknüpfte User-ID"
                      />
                      <v-btn
                        size="small"
                        variant="text"
                        class="px-0"
                        @click="openUserSearch(dozent.user_id || dozent.email || dozent.name)"
                      >
                        Zur User-Suche
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-btn color="primary" @click="createDozent">Speichern</v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
              <v-alert
                v-else-if="selectedWebinar"
                type="info"
                variant="tonal"
                class="mb-3"
              >
                Für dieses Webinar ist bereits ein Dozent hinterlegt. Bitte oben im Bereich
                „Dozent & Termine" bearbeiten.
              </v-alert>

              <v-expansion-panel>
                <v-expansion-panel-title>Webinar-Termin anlegen</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-text-field v-model.number="termin.wter_webinar_id" type="number" label="Webinar-ID" />
                  <v-text-field v-model="termin.wter_beginn" label="Beginn (YYYY-MM-DD HH:mm:ss)" />
                  <v-text-field v-model.number="termin.wter_count_max" type="number" label="Max. Teilnehmer" />
                  <v-btn color="primary" @click="createTermin">Speichern</v-btn>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-alert
              v-if="selectedTerminId && !canCreateTeilnehmer"
              type="info"
              variant="tonal"
              class="mt-3"
            >
              Teilnehmer können nur unter einer Termin-URL angelegt werden
              (z. B. <code>/admin-tools/6/39</code>).
            </v-alert>
          </v-window-item>

          <!-- ===================== TAB: USER ===================== -->
          <v-window-item value="user">
            <v-card variant="outlined" class="mb-4">
              <v-card-title>User suchen</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="userSearchTerm"
                      label="User suchen (ID, Vorname, Name, E-Mail)"
                      clearable
                      @keyup.enter="searchUsers"
                    />
                  </v-col>
                  <v-col cols="12" md="4" class="d-flex align-center">
                    <v-btn
                      color="primary"
                      :loading="isUserSearching"
                      :disabled="isUserSearching"
                      @click="searchUsers"
                    >
                      User suchen
                    </v-btn>
                  </v-col>
                </v-row>

                <div v-if="userSearchResults.length === 0" class="text-medium-emphasis">
                  Keine User-Ergebnisse. Suche oben nach ID, Name oder E-Mail.
                </div>
                <v-expansion-panels
                  v-else
                  v-model="openUserPanels"
                  multiple
                  variant="accordion"
                  @update:model-value="onUserPanelsChange"
                >
                  <v-expansion-panel
                    v-for="user in userSearchResults"
                    :key="user.user_id"
                    :value="user.user_id"
                  >
                    <v-expansion-panel-title>
                      <div class="w-100 d-flex flex-wrap justify-space-between align-center ga-2">
                        <div>
                          <div>
                            {{ [user.user_vorname, user.user_name].filter(Boolean).join(' ') || '-' }}
                            (ID: {{ user.user_id }})
                          </div>
                          <div class="text-caption text-medium-emphasis">{{ user.user_email || '-' }}</div>
                        </div>
                        <div class="d-flex flex-wrap ga-2">
                          <v-chip
                            v-if="Number(user.user_is_admin || 0) === 1"
                            size="small"
                            color="primary"
                            variant="flat"
                          >
                            Admin
                          </v-chip>
                          <v-chip
                            v-if="Number(user.user_is_dozent || 0) === 1"
                            size="small"
                            color="deep-purple"
                            variant="flat"
                          >
                            Dozent
                          </v-chip>
                          <v-chip size="small" variant="outlined">
                            User-ID: {{ user.user_id }}
                          </v-chip>
                        </div>
                      </div>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div class="d-flex align-center ga-2 mb-3">
                        <span class="text-subtitle-2">Teilnahmen</span>
                        <v-btn
                          size="x-small"
                          variant="text"
                          :loading="isUserTeilnahmenLoading(user.user_id)"
                          @click="loadUserTeilnahmen(user.user_id, true)"
                        >
                          Neu laden
                        </v-btn>
                      </div>
                      <div
                        v-if="isUserTeilnahmenLoading(user.user_id)"
                        class="d-inline-flex align-center ga-2 text-medium-emphasis"
                      >
                        <v-progress-circular indeterminate size="18" width="2" color="primary" />
                        <span>Teilnahmen werden geladen...</span>
                      </div>
                      <div
                        v-else-if="getUserTeilnahmen(user.user_id).length === 0"
                        class="text-medium-emphasis"
                      >
                        Keine Teilnahmen für diesen User.
                      </div>
                      <div v-else class="d-flex flex-column ga-3">
                        <v-card
                          v-for="row in getUserTeilnahmen(user.user_id)"
                          :key="row.wteil_id"
                          variant="tonal"
                        >
                          <v-card-text>
                            <div class="d-flex flex-wrap justify-space-between align-center ga-2 mb-2">
                              <div>
                                <div class="text-subtitle-2">
                                  {{ row.webi_title || `Webinar ${row.webi_id}` }}
                                </div>
                                <div class="text-caption text-medium-emphasis">
                                  Termin {{ row.wter_id }} · {{ row.wter_beginn || '-' }}
                                  · Dozent: {{ row.dozent_name || '-' }}
                                </div>
                              </div>
                              <div class="d-flex flex-wrap ga-2">
                                <v-chip
                                  v-if="Number(row.wteil_is_admin || 0) === 1"
                                  size="small"
                                  color="primary"
                                  variant="flat"
                                >
                                  Admin
                                </v-chip>
                                <v-chip
                                  v-if="Number(row.wteil_is_dozent || 0) === 1"
                                  size="small"
                                  color="deep-purple"
                                  variant="flat"
                                >
                                  Dozent
                                </v-chip>
                              </div>
                            </div>
                            <div
                              v-if="buildTeilnehmerLink(row)"
                              class="d-flex flex-wrap align-center ga-2"
                            >
                              <v-btn
                                size="small"
                                color="primary"
                                @click="openExternalLink(buildTeilnehmerLink(row))"
                              >
                                {{ getTeilnehmerLinkLabel(row) }} öffnen
                              </v-btn>
                              <v-btn
                                size="small"
                                variant="outlined"
                                @click="copyToClipboard(buildTeilnehmerLink(row), `${getTeilnehmerLinkLabel(row)} kopiert.`)"
                              >
                                Link kopieren
                              </v-btn>
                              <span class="text-body-2 text-medium-emphasis">
                                {{ buildTeilnehmerLink(row) }}
                              </span>
                            </div>
                            <div v-else class="text-body-2 text-medium-emphasis">
                              Kein Link verfügbar.
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </v-card>

            <v-card variant="outlined">
              <v-card-title>Neuen User anlegen</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="userCreate.user_vorname" label="Vorname" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="userCreate.user_name" label="Nachname" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="userCreate.user_email" label="E-Mail" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="userCreate.user_password"
                      label="Passwort (optional)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-switch
                      v-model="userCreate.user_is_admin"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      label="System-Admin"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-switch
                      v-model="userCreate.user_is_dozent"
                      :true-value="1"
                      :false-value="0"
                      color="primary"
                      label="Dozent"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn color="primary" @click="createUser">User anlegen</v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>

        <!-- ===================== DIALOG: TEILNEHMER ANLEGEN ===================== -->
        <v-dialog v-model="showTeilnehmerDialog" max-width="720">
          <v-card>
            <v-card-title>Teilnehmer hinzufügen</v-card-title>
            <v-card-text>
              <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                Hier nur den Teilnehmer anlegen. Die Verknüpfung mit einem User
                erfolgt anschließend in der Teilnehmer-Liste über das Feld „User-ID".
              </v-alert>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="teilnehmer.wteil_termin_id" type="number" label="Termin-ID" readonly />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model.number="teilnehmer.wteil_webinar_id" type="number" label="Webinar-ID" readonly />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="teilnehmer.wteil_pers_name" label="Name *" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="teilnehmer.wteil_pers_email" label="E-Mail" />
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption mb-2">Rolle</div>
                  <v-btn-toggle
                    v-model="teilnehmerRole"
                    color="primary"
                    mandatory
                    variant="outlined"
                    divided
                  >
                    <v-btn value="audience">Audience</v-btn>
                    <v-btn value="dozent" color="green">Dozent</v-btn>
                    <v-btn value="admin" color="orange-darken-2">Admin</v-btn>
                  </v-btn-toggle>
                  <div
                    v-if="teilnehmerRole === 'dozent' && hasDozentTeilnehmerForSelectedTermin"
                    class="text-caption text-error mt-2"
                  >
                    Für diesen Termin existiert bereits ein Dozent.
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="px-6 pb-4">
              <v-spacer />
              <v-btn variant="text" :disabled="isTeilnehmerCreating" @click="showTeilnehmerDialog = false">
                Abbrechen
              </v-btn>
              <v-btn
                color="primary"
                :loading="isTeilnehmerCreating"
                :disabled="isTeilnehmerCreating || (teilnehmerRole === 'dozent' && hasDozentTeilnehmerForSelectedTermin)"
                @click="createTeilnehmer"
              >
                Speichern
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-alert v-if="message" type="success" variant="tonal" class="mt-4">
          {{ message }}
        </v-alert>
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const baseCalls = inject('baseCalls');
const route = useRoute();
const router = useRouter();

const activeTab = ref('webinare');
const message = ref('');
const error = ref('');
const webinareRows = ref([]);
const isWebinareLoading = ref(false);
const showDozentEdit = ref(false);
const showTeilnehmerDialog = ref(false);
const teilnehmerRole = ref('audience');
const isTeilnehmerLoading = ref(false);
const isTeilnehmerCreating = ref(false);
const teilnehmerList = ref([]);
const teilnehmerEditRows = ref([]);
const teilnehmerSavingIds = ref([]);
const openTeilnehmerPanels = ref([]);
const selectedWebinarId = ref(null);
const selectedTerminId = ref(null);
const userSearchTerm = ref('');
const userSearchResults = ref([]);
const isUserSearching = ref(false);
const openUserPanels = ref([]);
const userTeilnahmen = ref({});
const openPanels = ref([]);
const appBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';

const dozent = ref({
  name: '',
  email: '',
  image: '',
  user_id: 0
});

const termin = ref({
  wter_webinar_id: 0,
  wter_beginn: '',
  wter_count_max: 100
});

const teilnehmer = ref({
  wteil_termin_id: 0,
  wteil_webinar_id: 0,
  wteil_user_id: 0,
  wteil_pers_name: '',
  wteil_pers_email: '',
  wteil_is_admin: 0,
  wteil_is_dozent: 0
});

const dozentEdit = ref({
  id: 0,
  name: '',
  email: '',
  image: '',
  user_id: 0
});

const userCreate = ref({
  user_vorname: '',
  user_name: '',
  user_email: '',
  user_password: '',
  user_is_admin: 0,
  user_is_dozent: 0
});

const webinarGroups = computed(() => {
  const byWebinar = new Map();
  webinareRows.value.forEach((row) => {
    const webiId = Number(row.webi_id || row.wter_webinar_id || 0);
    if (!webiId) return;
    if (!byWebinar.has(webiId)) {
      byWebinar.set(webiId, {
        webi_id: webiId,
        webi_title: row.webi_title || `Webinar ${webiId}`,
        dozent_id: row.dozent_id || 0,
        dozent_name: row.dozent_name || row.webi_dozent || '',
        dozent_email: row.dozent_email || '',
        dozent_image: row.dozent_image || '',
        dozent_user_id: Number(row.dozent_user_id || 0),
        termine: []
      });
    }
    byWebinar.get(webiId).termine.push(row);
  });
  return Array.from(byWebinar.values());
});

const selectedWebinar = computed(() =>
  webinarGroups.value.find((w) => w.webi_id === selectedWebinarId.value) || null
);

const selectedTermin = computed(() =>
  (selectedWebinar.value?.termine || []).find((t) => Number(t.wter_id || 0) === selectedTerminId.value) || null
);

const terminStats = computed(() =>
  (selectedWebinar.value?.termine || []).map((terminItem) => ({
    wter_id: Number(terminItem.wter_id || 0),
    wter_webinar_id: Number(terminItem.wter_webinar_id || terminItem.webi_id || selectedWebinarId.value || 0),
    wter_beginn: terminItem.wter_beginn || '',
    teilnehmer_count: Number(terminItem.teilnehmer_count || 0),
    teilnehmer_user_admin_count: Number(terminItem.teilnehmer_user_admin_count || 0),
    teilnehmer_user_dozent_count: Number(terminItem.teilnehmer_user_dozent_count || 0),
    dozentLink: buildDozentLink(terminItem),
    superLink: buildSuperLink(terminItem),
  }))
);

const hasExistingDozentForSelectedWebinar = computed(() =>
  !!(selectedWebinar.value && Number(selectedWebinar.value.dozent_id || 0) > 0)
);

const hasRouteSelection = computed(() =>
  !!route.params.webi_id || !!route.params.termin_id
);

const canCreateTeilnehmer = computed(() =>
  Number(route.params.webi_id || 0) > 0 && Number(route.params.termin_id || 0) > 0
);

const hasDozentTeilnehmerForSelectedTermin = computed(() =>
  teilnehmerList.value.some((row) => Number(row?.wteil_is_dozent || 0) === 1)
);

function resetMessages() {
  message.value = '';
  error.value = '';
}

function buildDozentLink(terminItem) {
  const wteilId = Number(terminItem?.dozent_link_wteil_id || 0);
  const hash = String(terminItem?.dozent_link_hash || '').trim();
  if (!appBaseUrl || !wteilId || !hash) {
    return '';
  }
  return `${appBaseUrl}/dozent/${wteilId}/${hash}`;
}

function buildSuperLink(terminItem) {
  const wterId = Number(terminItem?.wter_id || 0);
  const wteilId = Number(terminItem?.super_link_wteil_id || 0);
  if (!appBaseUrl || !wterId || !wteilId) {
    return '';
  }
  return `${appBaseUrl}/super/${wterId}/${wteilId}`;
}

function buildTeilnehmerLink(row) {
  const wteilId = Number(row?.wteil_id || 0);
  const hash = String(row?.wteil_audience_hash || '').trim();
  if (!appBaseUrl || !wteilId || !hash) {
    return '';
  }
  if (Number(row?.wteil_is_dozent || 0) === 1) {
    return `${appBaseUrl}/dozent/${wteilId}/${hash}`;
  }
  const browserToken = String(row?.wteil_browser_token || '').trim();
  return browserToken
    ? `${appBaseUrl}/watch/${wteilId}/${hash}/${browserToken}`
    : `${appBaseUrl}/watch/${wteilId}/${hash}`;
}

function getTeilnehmerLinkLabel(row) {
  return Number(row?.wteil_is_dozent || 0) === 1 ? 'Dozenten-Link' : 'Watch-Link';
}

function getTeilnehmerMarkerClass(row) {
  const isAdmin = Number(row?.display_is_admin || 0) === 1;
  const isDozent = Number(row?.display_is_dozent || 0) === 1;
  if (isAdmin && isDozent) {
    return 'teilnehmer-marker teilnehmer-marker--admin-dozent';
  }
  if (isAdmin) {
    return 'teilnehmer-marker teilnehmer-marker--admin';
  }
  if (isDozent) {
    return 'teilnehmer-marker teilnehmer-marker--dozent';
  }
  return 'teilnehmer-marker';
}

function isTeilnehmerSaving(wteilId) {
  return teilnehmerSavingIds.value.includes(Number(wteilId || 0));
}

function openExternalLink(url) {
  if (!url || typeof window === 'undefined') {
    return;
  }
  window.open(url, '_blank');
}

async function copyToClipboard(text, successText = 'Link kopiert.') {
  resetMessages();
  if (!text) {
    error.value = 'Kein Link verfügbar.';
    return;
  }
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      message.value = successText;
      return;
    }
    throw new Error('Clipboard API nicht verfügbar');
  } catch (e) {
    error.value = 'Link konnte nicht kopiert werden.';
  }
}

function normalizeTeilnehmerRow(row) {
  const userIsAdmin = Number(row.user_is_admin || 0);
  const userIsDozent = Number(row.user_is_dozent || 0);
  const teilnehmerIsAdmin = Number(row.wteil_is_admin || 0);
  const teilnehmerIsDozent = Number(row.wteil_is_dozent || 0);
  return {
    ...row,
    wteil_id: Number(row.wteil_id || 0),
    wteil_user_id: Number(row.wteil_user_id || 0),
    wteil_is_admin: teilnehmerIsAdmin,
    wteil_is_dozent: teilnehmerIsDozent,
    user_is_admin: userIsAdmin,
    user_is_dozent: userIsDozent,
    display_is_admin: userIsAdmin || teilnehmerIsAdmin ? 1 : 0,
    display_is_dozent: userIsDozent || teilnehmerIsDozent ? 1 : 0,
  };
}

function syncTeilnehmerEdits() {
  teilnehmerEditRows.value = teilnehmerList.value
    .map((row) => normalizeTeilnehmerRow(row))
    .sort((a, b) => {
      const rankA = a.display_is_admin === 1 ? 0 : a.display_is_dozent === 1 ? 1 : 2;
      const rankB = b.display_is_admin === 1 ? 0 : b.display_is_dozent === 1 ? 1 : 2;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return a.wteil_id - b.wteil_id;
    });
  openTeilnehmerPanels.value = [];
}

function openTeilnehmerDialog() {
  resetTeilnehmerForm();
  showTeilnehmerDialog.value = true;
}

function resetTeilnehmerForm() {
  teilnehmer.value = {
    wteil_termin_id: Number(route.params.termin_id || selectedTerminId.value || 0),
    wteil_webinar_id: Number(route.params.webi_id || selectedWebinarId.value || 0),
    wteil_user_id: 0,
    wteil_pers_name: '',
    wteil_pers_email: '',
    wteil_is_admin: 0,
    wteil_is_dozent: 0
  };
  teilnehmerRole.value = 'audience';
}

function openUserSearch(seedValue = '') {
  activeTab.value = 'user';
  userSearchTerm.value = String(seedValue || '').trim();
  if (userSearchTerm.value) {
    searchUsers();
  }
}

function goToOverview() {
  router.push({ name: 'admin-tools', params: {} });
}

function toggleDozentEdit() {
  showDozentEdit.value = !showDozentEdit.value;
}

async function loadWebinare() {
  error.value = '';
  isWebinareLoading.value = true;
  try {
    const webiId = Number(route.params.webi_id || 0);
    const response = await baseCalls.webinarAdminPost({
      action: 'adminGetWebinare',
      webi_id: webiId || undefined,
    });
    webinareRows.value = Array.isArray(response?.data?.webinare) ? response.data.webinare : [];
  } catch (e) {
    error.value = 'Webinare konnten nicht geladen werden.';
    webinareRows.value = [];
  } finally {
    isWebinareLoading.value = false;
  }
}

function selectWebinar(webinar) {
  selectedWebinarId.value = webinar.webi_id;
  selectedTerminId.value = null;
  showDozentEdit.value = false;
  teilnehmerRole.value = 'audience';
  teilnehmerList.value = [];
  teilnehmerEditRows.value = [];
  termin.value.wter_webinar_id = webinar.webi_id;
  router.push({ name: 'admin-tools', params: { webi_id: String(webinar.webi_id) } });
}

async function selectTermin(terminItem) {
  selectedTerminId.value = Number(terminItem.wter_id);
  teilnehmer.value.wteil_termin_id = Number(terminItem.wter_id);
  teilnehmer.value.wteil_webinar_id = Number(terminItem.wter_webinar_id || terminItem.webi_id || selectedWebinarId.value || 0);
  showTeilnehmerDialog.value = false;
  router.push({
    name: 'admin-tools',
    params: {
      webi_id: String(teilnehmer.value.wteil_webinar_id),
      termin_id: String(selectedTerminId.value),
    },
  });
  await loadTeilnehmer();
}

async function loadTeilnehmer() {
  if (!selectedTerminId.value) return;
  error.value = '';
  isTeilnehmerLoading.value = true;
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminGetTeilnehmerByTerminId',
      wteil_termin_id: selectedTerminId.value
    });
    teilnehmerList.value = Array.isArray(response?.data?.teilnehmer) ? response.data.teilnehmer : [];
    syncTeilnehmerEdits();
  } catch (e) {
    error.value = 'Teilnehmer konnten nicht geladen werden.';
    teilnehmerList.value = [];
    teilnehmerEditRows.value = [];
  } finally {
    isTeilnehmerLoading.value = false;
  }
}

async function searchUsers() {
  resetMessages();
  const term = String(userSearchTerm.value || '').trim();
  if (!term) {
    error.value = 'Bitte einen Suchbegriff eingeben.';
    return;
  }
  isUserSearching.value = true;
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminSearchUsers',
      term
    });
    userSearchResults.value = Array.isArray(response?.data?.users) ? response.data.users : [];
    openUserPanels.value = [];
    userTeilnahmen.value = {};
    if (userSearchResults.value.length === 0) {
      message.value = 'Keine User gefunden.';
    }
  } catch (e) {
    error.value = 'User konnten nicht gesucht werden.';
    userSearchResults.value = [];
  } finally {
    isUserSearching.value = false;
  }
}

function getUserTeilnahmen(userId) {
  return userTeilnahmen.value[Number(userId || 0)]?.rows || [];
}

function isUserTeilnahmenLoading(userId) {
  return !!userTeilnahmen.value[Number(userId || 0)]?.loading;
}

async function loadUserTeilnahmen(userId, force = false) {
  const id = Number(userId || 0);
  if (!id) return;
  const existing = userTeilnahmen.value[id];
  if (existing && existing.loaded && !force) {
    return;
  }
  userTeilnahmen.value = {
    ...userTeilnahmen.value,
    [id]: { loading: true, loaded: existing?.loaded || false, rows: existing?.rows || [] }
  };
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminGetTeilnehmerByUserId',
      user_id: id
    });
    const rows = Array.isArray(response?.data?.teilnehmer) ? response.data.teilnehmer : [];
    userTeilnahmen.value = {
      ...userTeilnahmen.value,
      [id]: { loading: false, loaded: true, rows }
    };
  } catch (e) {
    userTeilnahmen.value = {
      ...userTeilnahmen.value,
      [id]: { loading: false, loaded: true, rows: [] }
    };
    error.value = 'Teilnahmen konnten nicht geladen werden.';
  }
}

function onUserPanelsChange(openIds) {
  (Array.isArray(openIds) ? openIds : []).forEach((id) => loadUserTeilnahmen(id));
}

async function createUser() {
  resetMessages();
  if (!userCreate.value.user_email) {
    error.value = 'E-Mail ist erforderlich.';
    return;
  }
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminCreateUser',
      ...userCreate.value
    });
    if (response?.data?.success) {
      message.value = `User angelegt (ID: ${response.data.id})`;
      userSearchTerm.value = String(response.data.id);
      await searchUsers();
      userCreate.value = {
        user_vorname: '',
        user_name: '',
        user_email: '',
        user_password: '',
        user_is_admin: 0,
        user_is_dozent: 0
      };
    } else {
      error.value = response?.data?.message || 'User konnte nicht angelegt werden.';
    }
  } catch (e) {
    error.value = 'User konnte nicht angelegt werden.';
  }
}

async function createDozent() {
  if (hasExistingDozentForSelectedWebinar.value) {
    error.value = 'Dozent ist bereits vorhanden. Bitte Dozent bearbeiten.';
    return;
  }
  resetMessages();
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminCreateDozent',
      dozent_name: dozent.value.name,
      dozent_email: dozent.value.email,
      dozent_image: dozent.value.image,
      dozent_user_id: Number(dozent.value.user_id || 0)
    });
    if (response?.data?.success) {
      message.value = `Dozent angelegt (ID: ${response.data.id})`;
      dozent.value = {
        name: '',
        email: '',
        image: '',
        user_id: 0
      };
      await loadWebinare();
      await applyRouteSelection();
    } else {
      error.value = response?.data?.message || 'Dozent konnte nicht angelegt werden.';
    }
  } catch (e) {
    error.value = 'Dozent konnte nicht angelegt werden.';
  }
}

async function createTermin() {
  resetMessages();
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminCreateTermin',
      wter_webinar_id: termin.value.wter_webinar_id,
      wter_beginn: termin.value.wter_beginn,
      wter_count_max: termin.value.wter_count_max
    });
    if (response?.data?.success) {
      message.value = `Termin angelegt (ID: ${response.data.id})`;
      await loadWebinare();
    } else {
      error.value = response?.data?.message || 'Termin konnte nicht angelegt werden.';
    }
  } catch (e) {
    error.value = 'Termin konnte nicht angelegt werden.';
  }
}

async function createTeilnehmer() {
  if (!canCreateTeilnehmer.value) {
    error.value = 'Teilnehmer nur in /admin-tools/:webi_id/:termin_id anlegen.';
    return;
  }
  resetMessages();
  if (!String(teilnehmer.value.wteil_pers_name || '').trim()) {
    error.value = 'Bitte einen Namen für den Teilnehmer angeben.';
    return;
  }
  teilnehmer.value.wteil_is_admin = teilnehmerRole.value === 'admin' ? 1 : 0;
  teilnehmer.value.wteil_is_dozent = teilnehmerRole.value === 'dozent' ? 1 : 0;
  isTeilnehmerCreating.value = true;
  if (teilnehmerRole.value === 'dozent') {
    await loadTeilnehmer();
    if (hasDozentTeilnehmerForSelectedTermin.value) {
      error.value = 'Für diesen Termin existiert bereits ein Dozent.';
      isTeilnehmerCreating.value = false;
      return;
    }
  }
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminCreateTeilnehmer',
      ...teilnehmer.value
    });
    if (response?.data?.success) {
      message.value = `Teilnehmer angelegt (ID: ${response.data.id})`;
      showTeilnehmerDialog.value = false;
      resetTeilnehmerForm();
      await loadTeilnehmer();
    } else {
      error.value = response?.data?.message || 'Teilnehmer konnte nicht angelegt werden.';
    }
  } catch (e) {
    error.value = 'Teilnehmer konnte nicht angelegt werden.';
  } finally {
    isTeilnehmerCreating.value = false;
  }
}

async function updateTeilnehmer(row) {
  resetMessages();
  const wteilId = Number(row.wteil_id || 0);
  if (isTeilnehmerSaving(wteilId)) {
    return;
  }
  teilnehmerSavingIds.value = [...teilnehmerSavingIds.value, wteilId];
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminUpdateTeilnehmer',
      wteil_id: wteilId,
      wteil_user_id: Number(row.wteil_user_id || 0),
      wteil_pers_name: row.wteil_pers_name || '',
      wteil_pers_email: row.wteil_pers_email || '',
      wteil_is_admin: Number(row.wteil_is_admin || 0),
      wteil_is_dozent: Number(row.wteil_is_dozent || 0),
    });
    if (response?.data?.success) {
      message.value = `Teilnehmer ${row.wteil_id} aktualisiert.`;
      await loadTeilnehmer();
    } else {
      error.value = response?.data?.message || 'Teilnehmer konnte nicht aktualisiert werden.';
    }
  } catch (e) {
    error.value = 'Teilnehmer konnte nicht aktualisiert werden.';
  } finally {
    teilnehmerSavingIds.value = teilnehmerSavingIds.value.filter((id) => id !== wteilId);
  }
}

async function updateDozent() {
  resetMessages();
  if (!dozentEdit.value.id) {
    error.value = 'Kein Dozent zum Bearbeiten gefunden.';
    return;
  }
  try {
    const response = await baseCalls.webinarAdminPost({
      action: 'adminUpdateDozent',
      dozent_id: dozentEdit.value.id,
      dozent_name: dozentEdit.value.name,
      dozent_email: dozentEdit.value.email,
      dozent_image: dozentEdit.value.image,
      dozent_user_id: Number(dozentEdit.value.user_id || 0),
      dozent_status: 1
    });
    if (response?.data?.success) {
      message.value = 'Dozent aktualisiert.';
      await loadWebinare();
      await applyRouteSelection();
    } else {
      error.value = response?.data?.message || 'Dozent konnte nicht aktualisiert werden.';
    }
  } catch (e) {
    error.value = 'Dozent konnte nicht aktualisiert werden.';
  }
}

onMounted(async () => {
  await loadWebinare();
  await applyRouteSelection();
});

async function applyRouteSelection() {
  const webiId = Number(route.params.webi_id || 0);
  const terminId = Number(route.params.termin_id || 0);
  if (!webiId) {
    selectedWebinarId.value = null;
    selectedTerminId.value = null;
    showDozentEdit.value = false;
    showTeilnehmerDialog.value = false;
    teilnehmerRole.value = 'audience';
    teilnehmerList.value = [];
    teilnehmerEditRows.value = [];
    return;
  }

  selectedWebinarId.value = webiId;
  showDozentEdit.value = false;
  const webinar = webinarGroups.value.find((w) => w.webi_id === webiId);
  if (!webinar) return;
  dozentEdit.value = {
    id: Number(webinar.dozent_id || webinar.termine?.[0]?.dozent_id || 0),
    name: webinar.dozent_name || '',
    email: webinar.dozent_email || '',
    image: webinar.dozent_image || '',
    user_id: Number(webinar.dozent_user_id || webinar.termine?.[0]?.dozent_user_id || 0)
  };

  termin.value.wter_webinar_id = webiId;
  if (terminId) {
    const terminItem = webinar.termine.find((t) => Number(t.wter_id) === terminId);
    if (terminItem) {
      selectedTerminId.value = terminId;
      resetTeilnehmerForm();
      teilnehmer.value.wteil_termin_id = terminId;
      teilnehmer.value.wteil_webinar_id = Number(terminItem.wter_webinar_id || terminItem.webi_id || webiId);
      await loadTeilnehmer();
      return;
    }
  }

  selectedTerminId.value = null;
  teilnehmerList.value = [];
  teilnehmerEditRows.value = [];
}

watch(
  () => [route.params.webi_id, route.params.termin_id, webinareRows.value.length],
  async () => {
    await applyRouteSelection();
  }
);
</script>

<style scoped>
.teilnehmer-marker {
  border-left: 4px solid transparent;
  padding-left: 12px;
}

.teilnehmer-marker--admin {
  border-left-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}

.teilnehmer-marker--dozent {
  border-left-color: rgb(var(--v-theme-deep-purple));
  background: rgba(var(--v-theme-deep-purple), 0.08);
}

.teilnehmer-marker--admin-dozent {
  border-left-color: rgb(var(--v-theme-primary));
  background:
    linear-gradient(
      90deg,
      rgba(var(--v-theme-primary), 0.1) 0%,
      rgba(var(--v-theme-deep-purple), 0.08) 100%
    );
}
</style>
